import React, { useRef, useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { createSlug } from "./createSlug";
import useIntersectionObserver from "./hooks/useIntersectionObserver";
import { shouldLoadContentImmediately } from "./hooks/utils";
// import ThumbnailsHero from "./ThumbnailsHero";
import labels from "./labels.json";
import "./style.css";

const Portfolio = ({ template }) => {
  const sectionRef = useRef(null);
  const [contentLoaded, setContentLoaded] = useState(false);
  const [newFilteredItems, setNewFilteredItems] = useState([]);
  const [activeFilter, setActiveFilter] = useState("All");
  const [uniqueTemplates, setUniqueTemplates] = useState([]);
  const location = useLocation();
  const isPortfolioPage = location.pathname === "/portfolio";
  const movingBackgroundRef = useRef(null);
  const buttonRefs = useRef({});
  const [currentTag, setCurrentTag] = useState("");

  const colors = [
    "#ffffff",
    "#fc8e22",
    "#413e51",
    "#4f4f59",
    "#5d6e80",
    "#ffb531",
    "#1478c9",
    "#45286a",
    "#a325ea",
    "#661963",
    "#748e27",
    "#84a50c",
    "#69698c",
    "#a5b918",
    "#749aea",
    "#415d70",
    "#4b00f0",
    "#22a927",
    "#e524ba",
    "#04d94e",
    "#a5cc86",
    "#73dc4d",
    "#d7369a",
    "#82ae52",
    "#5be6a9",
    "#172bad",
    "#91b2df",
    "#2daff3",
    "#83119a",
    "#f9b978",
    "#a3f8ee",
    "#950c5f",
    "#4fecc3",
    "#adc017",
    "#b5191e",
    "#ffb531",
    "#39972e",
    "#de6f77",
    "#8d1d05",
    "#221e8d",
    "#528fd2",
    "#d31bd0",
    "#303ea2",
    "#24e947",
    "#f9b978",
    "#c49ec3",
    "#727412",
    "#114a1a",
    "#61b807",
    "#e8e4dc",
  ];

  // Function to get the background color based on the ID
  const getBackgroundColor = (id) => {
    return colors[(id - 1) % colors.length];
  };

  // Function to adjust the moving background
  const adjustMovingBackground = useCallback(() => {
    if (movingBackgroundRef.current && buttonRefs.current[activeFilter]) {
      if (window.innerWidth <= 768) {
        movingBackgroundRef.current.style.height = `${buttonRefs.current[activeFilter].offsetHeight}px`;
        movingBackgroundRef.current.style.top = `${buttonRefs.current[activeFilter].offsetTop}px`;
        movingBackgroundRef.current.style.width = null;
        movingBackgroundRef.current.style.left = null;
      } else {
        movingBackgroundRef.current.style.width = `${buttonRefs.current[activeFilter].offsetWidth}px`;
        movingBackgroundRef.current.style.left = `${buttonRefs.current[activeFilter].offsetLeft}px`;
        movingBackgroundRef.current.style.height = null;
        movingBackgroundRef.current.style.top = null;
      }
    }
  }, [activeFilter, movingBackgroundRef, buttonRefs]);

  // Reset the "All" filter when navigating back to the main portfolio page
  useEffect(() => {
    if (location.pathname === "/portfolio") {
      handleFilterClick("All");
    }
  }, [location.pathname]);

  // This useEffect updates the displayCount when the pathname changes
  useEffect(() => {
    if (isPortfolioPage) {
      setDisplayCount(labels.length);
    } else {
      setDisplayCount(7);
    }
  }, [isPortfolioPage]);

  useEffect(() => {
    const templates = labels.map((item) => item.template);
    setUniqueTemplates([...new Set(templates)]);
  }, [location.pathname]);

  useEffect(() => {
    if (movingBackgroundRef.current && buttonRefs.current.All) {
      if (window.innerWidth <= 768) {
        movingBackgroundRef.current.style.height = `${buttonRefs.current[activeFilter].offsetHeight}px`;
        movingBackgroundRef.current.style.top = `${buttonRefs.current[activeFilter].offsetTop}px`;
      } else {
        movingBackgroundRef.current.style.width = `${buttonRefs.current[activeFilter].offsetWidth}px`;
        movingBackgroundRef.current.style.left = `${buttonRefs.current[activeFilter].offsetLeft}px`;
      }
    }
    // Call it once during initial rendering
    adjustMovingBackground();

    // Call it whenever the window is resized
    window.addEventListener("resize", adjustMovingBackground);

    // Cleanup
    return () => {
      window.removeEventListener("resize", adjustMovingBackground);
    };
  }, [activeFilter, adjustMovingBackground, contentLoaded]);

  useEffect(() => {
    const fetchData = async () => {
      const currentPath = window.location.pathname;
      const pathParts = currentPath.split("/").filter(Boolean);

      if (
        pathParts.length === 3 &&
        pathParts[0] === "portfolio" &&
        pathParts[1] === "tags"
      ) {
        const tagToFilter = "#" + pathParts[2];
        setIsInTagsPage(true);
        setCurrentTag("#" + pathParts[2]);
        const newFilteredItems = [];

        for (const item of labels) {
          const jsonFilename = createSlug(item.title) + ".json";
          const fetchUrl = `/assets/portfoliopages/${item.template}/${jsonFilename}`;

          try {
            const response = await fetch(fetchUrl);
            if (response.status === 404) {
              continue; // Skip missing JSONs without logging
            }

            if (response.ok) {
              const text = await response.text();
              const itemJsonContent = JSON.parse(text);

              if (itemJsonContent.Hashtags) {
                for (const hashtag of itemJsonContent.Hashtags) {
                  if (hashtag.tag === tagToFilter) {
                    newFilteredItems.push({
                      ...item,
                      Hashtags: itemJsonContent.Hashtags, // Include the tags for debugging
                    });
                    break;
                  }
                }
              }
            }
          } catch (error) {
            if (error.message.includes("Unexpected token '<'")) {
              continue; // Likely a missing JSON file, so continue without logging
            }
            console.error("Error fetching JSON:", error);
          }
        }

        setNewFilteredItems(newFilteredItems); // Update the state with filtered items
      } else {
        setIsInTagsPage(false);
        setCurrentTag(""); // Reset the current tag
      }
    };

    fetchData();
  }, [template]);

  useEffect(() => {
    const currentPath = window.location.pathname;
    const pathParts = currentPath.split("/").filter(Boolean);

    if (
      pathParts.length === 3 &&
      pathParts[0] === "portfolio" &&
      pathParts[1] === "tags"
    ) {
      setIsInTagsPage(true);
    } else {
      setIsInTagsPage(false);
    }
  }, [location.pathname]);

  const handleIntersection = () => {
    setContentLoaded(true);
  };

  // Use the intersection observer, but bypass it on the homepage
  useIntersectionObserver(sectionRef, handleIntersection, {
    bypass: shouldLoadContentImmediately(),
  });

  const handleFilterClick = (tmpl) => {
    setActiveFilter(tmpl);
    if (movingBackgroundRef.current && buttonRefs.current[tmpl]) {
      // Check for screen size to toggle between vertical and horizontal layouts
      if (window.innerWidth <= 768) {
        movingBackgroundRef.current.style.height = `${buttonRefs.current[tmpl].offsetHeight}px`;
        movingBackgroundRef.current.style.top = `${buttonRefs.current[tmpl].offsetTop}px`;
        movingBackgroundRef.current.style.width = `${buttonRefs.current[tmpl].offsetWidth}px`; // Added this line
        movingBackgroundRef.current.style.left = `${buttonRefs.current[tmpl].offsetLeft}px`; // Added this line
      } else {
        movingBackgroundRef.current.style.width = `${buttonRefs.current[tmpl].offsetWidth}px`;
        movingBackgroundRef.current.style.left = `${buttonRefs.current[tmpl].offsetLeft}px`;
      }
    }
  };

  useIntersectionObserver(sectionRef, handleIntersection);

  const [displayCount, setDisplayCount] = useState(7);
  const [isInTagsPage, setIsInTagsPage] = useState(false);

  const handleLoadMore = () => {
    if (!isPortfolioPage) {
      setDisplayCount((prevCount) =>
        Math.min(
          prevCount + 7,
          isInTagsPage ? newFilteredItems.length : filteredLabels.length
        )
      );
    }
  };

  const filteredLabels = template
    ? labels.filter((item) => item.template === template)
    : labels;
  const displayLabels =
    activeFilter === "All"
      ? filteredLabels
      : filteredLabels.filter((item) => item.template === activeFilter);

  const shouldShowLoadMore = () => {
    if (isPortfolioPage) return false;
    if (isInTagsPage) {
      return displayCount < newFilteredItems.length && displayCount < 50;
    }
    return displayCount < filteredLabels.length && displayCount < 50;
  };

  return (
    <div
      ref={sectionRef}
      className={`loading-container${
        contentLoaded ? "" : " loading-spinner-container"
      }`}
    >
      {contentLoaded && location.pathname === "/portfolio" && (
        <>
          {/* <ThumbnailsHero /> */}
          <div className="filter-bar">
            <div ref={movingBackgroundRef} className="moving-background"></div>
            <button
              ref={(el) => (buttonRefs.current["All"] = el)}
              className={`filter-button ${
                activeFilter === "All" ? "active" : ""
              }`}
              onClick={() => handleFilterClick("All")}
            >
              All
            </button>
            {uniqueTemplates.map((tmpl, index) => (
              <button
                ref={(el) => (buttonRefs.current[tmpl] = el)}
                key={index}
                className={`filter-button ${
                  activeFilter === tmpl ? "active" : ""
                }`}
                onClick={() => handleFilterClick(tmpl)}
              >
                {labels.find((label) => label.template === tmpl).friendly}
              </button>
            ))}
          </div>
        </>
      )}
      {contentLoaded ? (
        <section
          id="portfolio"
          className={`project${
            location.pathname !== "/portfolio" ? "" : " reset-padding"
          }`}
        >
          <ul
            className={`project-list${
              window.location.pathname.includes("/tags/") ? " tag-header" : ""
            }`}
          >
            <li
              className={
                location.pathname === "/portfolio" ? "column-span-all" : ""
              }
            >
              <div className="project-content section-content">
                {isInTagsPage ? (
                  <>
                    <div className="section-subtitle">TAGS</div>
                    <h2 className="h3 section-title">
                      Filtering Tags:&nbsp;
                      <span className="tagtitle">{currentTag}</span>
                    </h2>
                    <div className="section-text">
                      This is a list of projects containing the {currentTag}{" "}
                      tag. You can reset the list by clicking the Portfolio link
                      again.
                    </div>
                  </>
                ) : (
                  <>
                    <div className="section-subtitle">My Works</div>
                    <h2 className="h3 section-title">
                      Dive into a World of Design Excellence!
                    </h2>
                    <div className="section-text">
                      My work showcases a commitment to creating products with
                      lasting value. Focused on user-centric design, I deliver
                      solutions that are both beautifully simple and
                      functionally elegant.
                    </div>
                  </>
                )}
              </div>
            </li>
            {(() => {
              const currentPath = window.location.pathname;
              const pathParts = currentPath.split("/").filter(Boolean);
              const isInTagsPage =
                pathParts.length === 3 &&
                pathParts[0] === "portfolio" &&
                pathParts[1] === "tags";
              const filteredLabelsToDisplay = isInTagsPage
                ? newFilteredItems
                : displayLabels;
              return filteredLabelsToDisplay
                .slice(0, displayCount)
                .map((item, index) => {
                  const originalIndex = labels.findIndex(
                    (label) => label.title === item.title
                  );
                  const backgroundColor = getBackgroundColor(originalIndex + 1); // Use the function to get the color
                  return (
                    <li
                      key={index}
                      className={
                        index >= displayCount - 7 && index < displayCount
                          ? "newly-loaded"
                          : ""
                      }
                    >
                      <Link
                        to={`/portfolio/${item.template}/${createSlug(
                          item.title
                        )}`}
                        className="project-card"
                      >
                        <div
                          className="card-banner"
                          style={{ backgroundColor }}
                        >
                          <img
                            alt={item.title}
                            className="w-100"
                            src={`/assets/images/project-${
                              originalIndex + 1
                            }.png`}
                          />
                        </div>
                        <div className="card-content">
                          <h3 className="h4 card-title">{item.title}</h3>
                          <time className="friendly-sub">{item.friendly}</time>
                        </div>
                      </Link>
                    </li>
                  );
                });
            })()}

            <li>
              {shouldShowLoadMore() && (
                <button className="load-more" onClick={handleLoadMore}>
                  Load more work
                  <div className="chevron-down"></div>
                </button>
              )}
            </li>
          </ul>
        </section>
      ) : (
        <div className="observer_spinner"></div>
      )}
    </div>
  );
};

export default Portfolio;
